import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import { useQuery, useApolloClient } from "react-apollo";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import { Table, Generic, Field, Control } from "rbx";
import { useSticky } from "react-table-sticky";
import { useLocalStorage } from "../../utils/hooks";

import { ourToast as toast } from "../../utils";

import Pagination from "./Pagination";
import Loading from "../Loading";

import "./DataTable.scss";

const DataTablePropTypes = {
  columns: PropTypes.array,
  dataQuery: PropTypes.object,
  name: PropTypes.string,
  onRowClick: PropTypes.func,
  where: PropTypes.object,
  initialSortBy: PropTypes.array,
  isFixedWidth: PropTypes.bool,
  hiddenColumns: PropTypes.array,
  isPaginated: PropTypes.bool,
  fetchPolicy: PropTypes.string,
  savePageIndex: PropTypes.bool
};

const DataTableDefaultProps = {
  initialSortBy: [],
  fetchPolicy: "cache-and-network"
};
const renderFunc = cell => {
  if (cell.value === null) return null;
  if (cell.value === "true" || cell.value === "false") {
    return (
      <Generic textColor={cell.value === "true" ? "success" : "danger"}>
        {cell.value === "true" ? "Yes" : "No"}
      </Generic>
    );
  }
  if (typeof cell.value === "boolean" && cell.column.id !== "Posted") {
    return (
      <Generic textColor={cell.value ? "success" : "danger"}>
        {cell.value ? "Yes" : "No"}
      </Generic>
    );
  }
  if (typeof cell.value === "boolean" && cell.column.id === "Posted") {
    return (
      <Generic textColor={cell.value ? "success" : "danger"}>
        {cell.value ? "Posted" : "Unposted"}
      </Generic>
    );
  }
  return cell.render("Cell");
};

const defaultPropGetter = () => {};

export function ReactTable({
  columns,
  data,
  loading = false,
  savePageIndex = false,
  onRowClick = () => {},
  onCellClick = () => {},
  initialSortBy = [],
  isFixedWidth = true,
  getRowProps = defaultPropGetter,
  updateData,
  hiddenColumns = [],
  handlePagePaginated,
  defaultPageSize,
  setDefaultPageSize,
  isPaginated,
  onChangeSort,
  displayPaginationControls = true
}) {
  const newData = data && Array.isArray(data) ? data : data.Payload;

  const [storedPageSize] = useLocalStorage(`DEFAULT_PAGE_SIZE`, 50, false);
  const [storedPageIndex, setStoredPageIndex] = useLocalStorage(
    `DEFAULT_PAGE_INDEX`,
    0,
    false
  );

  const tableState = {
    pageIndex: savePageIndex ? storedPageIndex : 0,
    sortBy: initialSortBy,
    hiddenColumns,
    pageSize: defaultPageSize || storedPageSize
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy }
  } = useTable(
    {
      columns,
      data: newData,
      initialState: { ...tableState },
      updateData,
      manualPagination: isPaginated,
      manualSortBy: isPaginated
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useSticky
  );
  const classes = [
    "data-table-container",
    isFixedWidth ? "is-fixed-width" : ""
  ];

  const handleSetPageSize = n => {
    setDefaultPageSize(n);
    setPageSize(n);
    if (isPaginated) {
      handlePagePaginated(null, n);
    }
  };

  const renderTableHeaderSort = useCallback(column => {
    if (column.isSorted) {
      return column.isSortedDesc ? " ▼" : " ▲";
    }
  }, []);

  useEffect(() => {
    if (isPaginated) {
      onChangeSort(sortBy);
    }
  }, [isPaginated, onChangeSort, sortBy]);

  const showPaginationControls =
    displayPaginationControls &&
    ((!!pageOptions?.length && pageOptions.length > 1) ||
      (isPaginated &&
        (data?.PageInfo?.NextPageUrl || data?.PageInfo?.PriorPageUrl)) ||
      newData.length === pageSize ||
      newData.length < pageSize);

  const onClickPrev = () => {
    if (isPaginated) {
      handlePagePaginated(-1);
    } else {
      previousPage();
    }
    if (savePageIndex) {
      setStoredPageIndex(prev => prev - 1);
    }
  };

  const onClickNext = () => {
    if (isPaginated) {
      handlePagePaginated(1);
    } else {
      nextPage();
    }
    if (savePageIndex) {
      setStoredPageIndex(prev => prev + 1);
    }
  };

  const prevButtonEnabled =
    isPaginated && data.PageInfo && data.PageInfo.PriorPageUrl
      ? data.PageInfo.StartRow !== 0
      : canPreviousPage;

  const nextButtonEnabled =
    isPaginated && data.PageInfo && data.PageInfo.NextPageUrl
      ? !data.PageInfo.IsLastPage
      : canNextPage;

  const currentPage =
    isPaginated && data.PageInfo
      ? Math.ceil(data.PageInfo.StartRow / defaultPageSize + 1)
      : pageIndex + 1;

  const totalPages =
    isPaginated && data.PageInfo
      ? Math.ceil(data.PageInfo.QueryCount / defaultPageSize)
      : pageOptions.length;

  const isSticky = columns.some(column => !!column.sticky);

  return (
    <div className={classes.join(" ")}>
      <div className="table-container">
        {loading && newData && newData.length === 0 ? (
          <Loading />
        ) : (
          <Table
            {...getTableProps()}
            bordered
            fullwidth
            hoverable
            narrow
            className={[isSticky && "sticky"].filter(Boolean).join(" ")}
          >
            <Table.Head className="header">
              {headerGroups.map(headerGroup => (
                <Table.Row {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <Table.Heading
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={
                        typeof column.maxWidth === "string"
                          ? { width: column.maxWidth }
                          : { maxWidth: column.maxWidth || 400 }
                      }
                    >
                      {column.render("Header")}
                      <span>{renderTableHeaderSort(column)}</span>
                    </Table.Heading>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body {...getTableBodyProps()} className="body">
              {page.map(
                (row, i) =>
                  prepareRow(row) || (
                    <Table.Row
                      {...row.getRowProps(getRowProps(row))}
                      onClick={() => onRowClick(row.original)}
                    >
                      {row.cells.map((cell, j) => (
                        <Table.Cell
                          {...cell.getCellProps()}
                          key={`row-${i}-cell-${j}`}
                          onClick={() => onCellClick(cell)}
                        >
                          {renderFunc(cell)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )
              )}
            </Table.Body>
          </Table>
        )}
      </div>
      {showPaginationControls && (
        <Field align="right" kind="group">
          <Control>
            <Pagination
              canNextPage={nextButtonEnabled}
              canPreviousPage={prevButtonEnabled}
              page={currentPage}
              pageSize={pageSize}
              pages={totalPages || "?"}
              setPageSize={handleSetPageSize}
              onClickNext={onClickNext}
              onClickPrev={onClickPrev}
            />
          </Control>
        </Field>
      )}
    </div>
  );
}

ReactTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  fetchData: PropTypes.func,
  loading: PropTypes.bool,
  pageCount: PropTypes.number,
  pageSize: PropTypes.number,
  onRowClick: PropTypes.func,
  savePageIndex: PropTypes.bool,
  onCellClick: PropTypes.func,
  initialSortBy: PropTypes.array,
  isFixedWidth: PropTypes.bool,
  getRowProps: PropTypes.func,
  defaultColumn: PropTypes.object,
  updateData: PropTypes.func,
  defaultPageSize: PropTypes.number,
  hiddenColumns: PropTypes.array,
  handlePagePaginated: PropTypes.func,
  setDefaultPageSize: PropTypes.func,
  isPaginated: PropTypes.bool,
  onChangeSort: PropTypes.func,
  displayPaginationControls: PropTypes.bool
};

ReactTable.defaultProps = {
  data: [],
  isPaginated: false,
  setDefaultPageSize: () => {}
};
function DataTable({
  columns,
  where,
  dataQuery,
  name,
  onRowClick,
  initialSortBy = [],
  isFixedWidth,
  hiddenColumns,
  isPaginated,
  fetchPolicy,
  savePageIndex = false
}) {
  const [result, setResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [orderByMulti, setOrderByMulti] = useState(initialSortBy);

  const [defaultPageSize, setDefaultPageSize] = useLocalStorage(
    `DEFAULT_PAGE_SIZE`,
    50,
    false
  );

  const client = useApolloClient();

  const { data, loading, error } = useQuery(dataQuery, {
    variables: isPaginated
      ? {
          where,
          orderByMulti: orderByMulti.map(
            x => `${x.id}_${x.desc ? "DESC" : "ASC"}`
          ),
          first: defaultPageSize
        }
      : {
          where
        },
    fetchPolicy
  });

  if (error) {
    console.error(error);
    toast.error("Error getting Data.");
  }

  const handleChangeSort = useCallback(async sortBy => {
    setOrderByMulti(sortBy);
  }, []);

  useEffect(() => {
    const correctData = isPaginated ? data?.[`${name}`] : data?.[`${name}s`];
    if (Array.isArray(isPaginated ? correctData?.Payload : correctData)) {
      setResult(correctData);
    }
  }, [data, isPaginated, name]);

  const handlePagePaginated = async (direction, resizePage = 0) => {
    setIsLoading(true);
    const {
      PageInfo: { NextPageUrl, PriorPageUrl }
    } = result;

    const variables = resizePage
      ? {
          where,
          first: resizePage
        }
      : {
          where:
            direction === 1
              ? { ...where, NextPageUrl }
              : { ...where, PriorPageUrl }
        };

    const { data: PagedData } = await client.query({
      query: dataQuery,
      variables
    });
    setResult(PagedData[`${name}`]);
    setIsLoading(false);
  };
  const memoizedColumns = useMemo(() => [...columns], [columns]);

  if (loading || isLoading || !data) return <Loading />;

  return (
    <ReactTable
      columns={memoizedColumns}
      data={result}
      defaultPageSize={defaultPageSize}
      handlePagePaginated={handlePagePaginated}
      hiddenColumns={hiddenColumns}
      initialSortBy={orderByMulti}
      isFixedWidth={isFixedWidth}
      isPaginated={isPaginated}
      loading={loading || isLoading}
      name={name}
      savePageIndex={savePageIndex}
      setDefaultPageSize={setDefaultPageSize}
      onChangeSort={handleChangeSort}
      onRowClick={onRowClick}
    />
  );
}

DataTable.propTypes = DataTablePropTypes;
DataTable.defaultProps = DataTableDefaultProps;
export default DataTable;
